/**
 * View-scoped presence filtering for multi-surface entities (e.g. a set of
 * derived tabs, all in ONE Yjs doc + one Awareness). Each client publishes
 * its current `viewId` into awareness local state (see
 * `usePublishAwarenessView`); a consumer that is itself on a view passes
 * that `viewId` to filter presence down to co-present-on-this-view peers.
 *
 * Single-surface entities (regular docs/canvases) pass no `viewId` — filtering
 * is OFF and presence is identical to before this feature.
 *
 * **Fail-closed:** when the local viewer IS filtering (has a `viewId`), a remote
 * whose state carries no `viewId` is DROPPED. A remote only survives if it
 * published the SAME view. (Chosen so per-view counts are crisp; in practice
 * every client in a multi-surface entity publishes a view.)
 */

interface ViewAwarenessState {
  viewId?: string;
}

/**
 * Should a remote awareness `state` be kept, given the local viewer's `viewId`?
 *
 * - `localViewId === undefined` → filtering off → keep everything.
 * - else keep iff `state.viewId === localViewId` (fail-closed on missing).
 */
export function keepStateForView(
  state: ViewAwarenessState,
  localViewId: string | undefined,
): boolean {
  if (localViewId === undefined) return true;
  return state.viewId === localViewId;
}
